import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import clsx from 'clsx';
import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { AutocompleteBase } from '../autocomplete';
import { FlowIcon } from '../flow-icon/FlowIcon';
import { InlineAutocompleteOption } from '../inline-autocomplete/InlineAutocomplete';
import { TextField } from '../react-hook-form';
import { Text } from '../text';

// TODO(jacob): Remove once react-hook-form setValue types are compatible after upgrade.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ReactHookFormBypassType = any;

const ROOT = makeRootClassName('InlineQuantifiedField');
const el = makeElementClassNameFactory(ROOT);

interface InlineQuantifiedFieldProps<T> {
  isDisabled?: boolean;
  label: string;
  valueName: Path<T>;
  uomName: Path<T>;
  save: (p: Path<T>) => void;
  required: boolean;
  uomOptions: InlineAutocompleteOption[];
  valuePlaceholder?: string;
  unitsPlaceholder?: string;
}

export const InlineQuantifiedField = <T extends FieldValues>(
  p: InlineQuantifiedFieldProps<T>
) => {
  const { watch, setValue, control } = useFormContext<T>();
  const value = watch(p.valueName);
  const uom = watch(p.uomName);

  const onSaveValue = () => {
    p.save(p.valueName);
  };

  const handleValueKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSaveValue();
    }
  };

  const onSaveUoM = (value: InlineAutocompleteOption) => {
    setValue(p.uomName, value.id as ReactHookFormBypassType);
    p.save(p.uomName);
  };

  const selectPlaceholder = (
    placeholder: string | undefined,
    label: string | undefined
  ) => {
    if (placeholder) {
      return placeholder;
    }
    if (label) {
      return `Add ${label.toLowerCase()}`;
    }
    return '';
  };
  const selectedValuePlaceholder = selectPlaceholder(
    p.valuePlaceholder,
    p.label
  );
  const selectedUnitsPlaceholder = selectPlaceholder(
    p.unitsPlaceholder,
    p.label
  );
  const selectedUoMValue = p.uomOptions.find((o) => o.id === uom)?.label || uom;

  return (
    <div className={clsx(ROOT)}>
      <div className={el`table`}>
        <div className={el`label`}>
          <Text type="body-sm">{p.label}</Text>
        </div>
        <div className={'flex items-center'}>
          <TextField
            isDisabled={p.isDisabled}
            placeholder={selectedValuePlaceholder}
            onKeyDown={handleValueKeyDown}
            control={control}
            name={p.valueName}
            onBlur={onSaveValue}
            type="number"
            size="medium"
            fieldWrapperClassName={clsx(
              el`input-wrapper`,
              value ? 'VALID' : 'INVALID',
              p.required && 'REQUIRED'
            )}
            transparentWithoutFocus
            placeholderClassName={clsx(
              el`placeholder`,
              value ? 'VALID' : 'INVALID',
              p.required && 'REQUIRED'
            )}
            aria-label={p.label}
          />
          <AutocompleteBase
            isDisabled={p.isDisabled}
            onChange={(_, value) => {
              onSaveUoM(value as InlineAutocompleteOption);
            }}
            value={selectedUoMValue}
            options={p.uomOptions}
            inputProps={{
              required: p.required,
            }}
            selectOnFocus
            size="small"
            compact
            blurOnSelect
            transparentWithoutFocus
            inputWrapperClassName={clsx(
              el`input-wrapper`,
              selectedUoMValue ? 'VALID' : 'INVALID',
              p.required && 'REQUIRED'
            )}
            placeholderClassName={clsx(
              el`placeholder`,
              selectedUoMValue ? 'VALID' : 'INVALID',
              p.required && 'REQUIRED'
            )}
            placeholder={selectedUnitsPlaceholder ?? 'Units'}
          />
          <FlowIcon fieldPath={p.valueName} />
        </div>
        {/* <TextField
          placeholder={valuePlaceholder}
          onKeyDown={handleUoMKeyDown}
          control={control}
          name={p.uomName}
          onBlur={onSaveUoM}
          size="small"
          fieldWrapperClassName={clsx(
            el`input-wrapper`,
            uom ? 'VALID' : 'INVALID',
            p.required && 'REQUIRED'
          )}
          transparentWithoutFocus
          placeholderClassName={clsx(
            el`placeholder`,
            value ? 'VALID' : 'INVALID',
            p.required && 'REQUIRED'
          )}
        /> */}
      </div>
    </div>
  );
};
